var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import _ from 'lodash';
import { livequeueConsultationService } from 'ziphy-web-shared/basic/api';
import { isClientApp } from 'ziphy-web-shared/basic/helpers';
import { Button } from 'ziphy-web-shared/basic/lib/buttons';
import { Dropdown, DropdownContent, useDropdown } from 'ziphy-web-shared/basic/lib/functional';
import { $loader, $windowSize } from 'ziphy-web-shared/basic/utils';
import { $router } from 'ziphy-web-shared/basic/utils/router';
import { apptMapActions } from '@config/appts';
import styles from './ApptButtons.module.scss';
const ApptButtons = ({ byRoute, className, parentAppt, isOpen = true }) => {
    var _a, _b;
    const { fullAppt, status, actions } = byRoute;
    const { t } = useTranslation();
    const [consultation, setConsultation] = useState(null);
    const targetConsultationId = useMemo(() => {
        var _a, _b;
        return ((_a = fullAppt === null || fullAppt === void 0 ? void 0 : fullAppt.appointment) === null || _a === void 0 ? void 0 : _a.liveqId) || ((_b = parentAppt === null || parentAppt === void 0 ? void 0 : parentAppt.appointment) === null || _b === void 0 ? void 0 : _b.liveqId) || null;
    }, [(_a = fullAppt === null || fullAppt === void 0 ? void 0 : fullAppt.appointment) === null || _a === void 0 ? void 0 : _a.liveqId, (_b = parentAppt === null || parentAppt === void 0 ? void 0 : parentAppt.appointment) === null || _b === void 0 ? void 0 : _b.liveqId]);
    const preparedList = useMemo(() => {
        let inPanel = [], inDropdown = [];
        let mapActions = apptMapActions({ fullAppt, status, actions });
        mapActions = _.orderBy(mapActions, ['visible'], ['desc']);
        mapActions.forEach((item) => {
            let hidden = false;
            if (item.visible < 0) {
                inDropdown.push(item);
                hidden = true;
            }
            else if (item.visible) {
                if (inPanel.length < ($windowSize.maxSM ? 0 : $windowSize.maxMD ? 1 : 2)) {
                    inPanel.push(item);
                }
                else {
                    inDropdown.push(item);
                    hidden = true;
                }
            }
            if (hidden) {
                inPanel.push(Object.assign(Object.assign({}, item), { hidden: true }));
            }
        });
        return { inPanel, inDropdown };
    }, [fullAppt, status, actions, $windowSize.maxMD, $windowSize.maxSM]);
    const handleAction = (item) => __awaiter(void 0, void 0, void 0, function* () {
        const fn = $loader.registerHandler('appt-handle-action-button-' + item.id, () => __awaiter(void 0, void 0, void 0, function* () {
            if (!_.isEmpty(item.actionProp)) {
                yield item.action(_.get(fullAppt, item.actionProp));
            }
            else {
                yield item.action();
            }
        }));
        yield fn();
    });
    const handleGoToConsultation = () => {
        if (isClientApp()) {
            if (!consultation)
                return;
            const chatId = consultation.chat_id;
            $router.history.push(`/orders/${targetConsultationId}/chat/${chatId}`);
        }
        else {
            $router.history.push(`/consultation/${targetConsultationId}`);
        }
    };
    const dropdownSource = useMemo(() => {
        const panelVisible = preparedList.inPanel.filter((x) => !x.hidden);
        const shouldAddConsultation = $windowSize.maxMD &&
            ((isClientApp() && !!consultation) || (!isClientApp() && !!targetConsultationId));
        const consultationItem = shouldAddConsultation
            ? {
                id: 'to_consultation',
                label: t('btn.to_consultation'),
                action: handleGoToConsultation,
                disabled: false,
                visible: 1,
                primary: false,
            }
            : null;
        const base = isOpen ? preparedList.inDropdown : [...preparedList.inDropdown, ...panelVisible];
        return consultationItem ? [consultationItem, ...base] : base;
    }, [
        preparedList.inDropdown,
        preparedList.inPanel,
        isOpen,
        $windowSize.maxSM,
        consultation,
        targetConsultationId,
        t,
    ]);
    const { filteredItems } = useDropdown(useMemo(() => dropdownSource.map((x) => (Object.assign(Object.assign({}, x), { onClick: x.action }))), [dropdownSource]));
    useEffect(() => {
        if (!isClientApp())
            return;
        if (!targetConsultationId)
            return;
        let ignore = false;
        const fetchConsultation = () => __awaiter(void 0, void 0, void 0, function* () {
            try {
                const resConsultation = yield livequeueConsultationService.getConsultationById(targetConsultationId);
                if (!ignore)
                    setConsultation(resConsultation);
            }
            catch (e) { }
        });
        void fetchConsultation();
        return () => {
            ignore = true;
        };
    }, [targetConsultationId]);
    return (_jsxs("div", Object.assign({ className: classNames(styles.wrapper, className) }, { children: [!$windowSize.maxMD && (consultation || (!isClientApp() && targetConsultationId)) && (_jsx(Button, Object.assign({ mode: 'secondary', onClick: handleGoToConsultation }, { children: t('btn.to_consultation') }))), isOpen &&
                preparedList.inPanel.map((item) => !item.hidden && (_jsx(Button, Object.assign({ mode: [item.primary ? 'primary' : 'secondary', 'sm'], onClick: () => handleAction(item), className: classNames(styles.button, item.marker && 'red_marker'), isLoading: $loader.isRunHandler('appt-handle-action-button-' + item.id), disabled: item.disabled }, { children: item.label }), item.id))), dropdownSource.length > 0 && (_jsx(Dropdown, Object.assign({ renderButton: ({ fieldRef, onToggle }) => (_jsx(Button, { ref: fieldRef, mode: ['secondary-icon', 'justIcon', 'sm'], onClick: onToggle, leftElement: 'moreDots' })), className: styles.dropdown, direction: ['bottom'] }, { children: _jsx(DropdownContent, { items: filteredItems }) })))] })));
};
export default observer(ApptButtons);
